import { RealtimeConfig } from './config';
import { Emitter } from './emitter';
import { SignalType } from './enums';
import {
  AdminStreamEvent,
  CommandResult,
  ConnectionState,
  PendingRequest,
  RealtimeEventMap,
  SimulatorStatus,
} from './events';
import {
  mapAssistanceRequested,
  mapAssistanceResolved,
  mapCommandResult,
  mapCommandStatus,
  mapEventChanged,
  mapExtensionRequested,
  mapExtensionResolved,
  mapPendingRequest,
  mapSessionState,
  mapSimulatorPresence,
  mapSimulatorStatus,
  warnOnLegacyCasing,
} from './event-mapper';
import { HttpClient } from './http-client';
import { SseClient } from './sse-client';
import { SseFrame } from './sse-parser';
export interface IssueCommandOptions {
  parameters?: string;
  expirySeconds?: number;
}
export class BayManagerRealtimeClient {
  private readonly emitter = new Emitter<RealtimeEventMap>();
  private readonly http: HttpClient;
  private readonly sse: SseClient;
  constructor(config: RealtimeConfig) {
    this.http = new HttpClient(config.baseApiUrl, config.getToken);
    this.sse = new SseClient(
      config,
      (frame) => this.handleFrame(frame),
      (state) => this.emitter.emit('connectionState', state),
    );
  }
  connect(companyId: number): void {
    this.sse.connect(`/BayManagerStream/status?companyId=${companyId}`);
  }
  disconnect(): void {
    this.sse.disconnect();
  }
  onCommandStatus(
    listener: (payload: RealtimeEventMap['commandStatus']) => void,
  ): () => void {
    return this.emitter.on('commandStatus', listener);
  }
  onSimulatorPresence(
    listener: (payload: RealtimeEventMap['simulatorPresence']) => void,
  ): () => void {
    return this.emitter.on('simulatorPresence', listener);
  }
  onEventChanged(
    listener: (payload: RealtimeEventMap['eventChanged']) => void,
  ): () => void {
    return this.emitter.on('eventChanged', listener);
  }
  onAssistanceRequested(
    listener: (payload: RealtimeEventMap['assistanceRequested']) => void,
  ): () => void {
    return this.emitter.on('assistanceRequested', listener);
  }
  onAssistanceResolved(
    listener: (payload: RealtimeEventMap['assistanceResolved']) => void,
  ): () => void {
    return this.emitter.on('assistanceResolved', listener);
  }
  onExtensionRequested(
    listener: (payload: RealtimeEventMap['extensionRequested']) => void,
  ): () => void {
    return this.emitter.on('extensionRequested', listener);
  }
  onExtensionResolved(
    listener: (payload: RealtimeEventMap['extensionResolved']) => void,
  ): () => void {
    return this.emitter.on('extensionResolved', listener);
  }
  onSessionStarted(
    listener: (payload: RealtimeEventMap['sessionStarted']) => void,
  ): () => void {
    return this.emitter.on('sessionStarted', listener);
  }
  onSessionEnded(
    listener: (payload: RealtimeEventMap['sessionEnded']) => void,
  ): () => void {
    return this.emitter.on('sessionEnded', listener);
  }
  onConnectionState(listener: (state: ConnectionState) => void): () => void {
    return this.emitter.on('connectionState', listener);
  }
  issueCommand(
    simulatorIdentifier: string,
    type: SignalType,
    options?: IssueCommandOptions,
  ): Promise<CommandResult> {
    return this.http
      .post<Parameters<typeof mapCommandResult>[0]>('/Commands', {
        simulatorIdentifier: simulatorIdentifier,
        type,
        parameters: options?.parameters,
        expirySeconds: options?.expirySeconds,
      })
      .then(mapCommandResult);
  }
  lockSimulator(
    simulatorIdentifier: string,
    options?: IssueCommandOptions,
  ): Promise<CommandResult> {
    return this.issueCommand(simulatorIdentifier, SignalType.LockDown, options);
  }
  unlockSimulator(
    simulatorIdentifier: string,
    options?: IssueCommandOptions,
  ): Promise<CommandResult> {
    return this.issueCommand(simulatorIdentifier, SignalType.Unlock, options);
  }
  async sendExtensionDecision(
    eventId: number,
    approved: boolean,
  ): Promise<void> {
    await this.http.post<void>('/LauncherRequest/extension/decision', {
      eventId: eventId,
      approved,
    });
  }
  async resolveAssistance(simulatorIdentifier: string): Promise<void> {
    await this.http.post<void>('/LauncherRequest/assistance/resolve', {
      simulatorIdentifier: simulatorIdentifier,
    });
  }
  getOnlineSimulators(companyId: number): Promise<SimulatorStatus[]> {
    return this.http
      .get<Parameters<typeof mapSimulatorStatus>[0][]>(
        `/SimulatorStatus?companyId=${companyId}`,
      )
      .then((rows) => rows.map(mapSimulatorStatus));
  }
  getPendingRequests(companyId: number): Promise<PendingRequest[]> {
    return this.http
      .get<Parameters<typeof mapPendingRequest>[0][]>(
        `/LauncherRequest/pending?companyId=${companyId}`,
      )
      .then((rows) => rows.map(mapPendingRequest));
  }
  private handleFrame(frame: SseFrame): void {
    warnOnLegacyCasing(frame.data);
    switch (frame.event) {
      case AdminStreamEvent.Connected:
      case AdminStreamEvent.Heartbeat:
        return;
      case AdminStreamEvent.CommandStatus:
        this.dispatch(frame, (data) =>
          this.emitter.emit('commandStatus', mapCommandStatus(data)),
        );
        return;
      case AdminStreamEvent.SimulatorOnline:
      case AdminStreamEvent.SimulatorOffline:
        this.dispatch(frame, (data) =>
          this.emitter.emit('simulatorPresence', mapSimulatorPresence(data)),
        );
        return;
      case AdminStreamEvent.EventChanged:
        this.dispatch(frame, (data) =>
          this.emitter.emit('eventChanged', mapEventChanged(data)),
        );
        return;
      case AdminStreamEvent.AssistanceRequested:
        this.dispatch(frame, (data) =>
          this.emitter.emit(
            'assistanceRequested',
            mapAssistanceRequested(data),
          ),
        );
        return;
      case AdminStreamEvent.AssistanceResolved:
        this.dispatch(frame, (data) =>
          this.emitter.emit('assistanceResolved', mapAssistanceResolved(data)),
        );
        return;
      case AdminStreamEvent.ExtensionRequested:
        this.dispatch(frame, (data) =>
          this.emitter.emit('extensionRequested', mapExtensionRequested(data)),
        );
        return;
      case AdminStreamEvent.ExtensionResolved:
        this.dispatch(frame, (data) =>
          this.emitter.emit('extensionResolved', mapExtensionResolved(data)),
        );
        return;
      case AdminStreamEvent.SessionStarted:
        this.dispatch(frame, (data) =>
          this.emitter.emit('sessionStarted', mapSessionState(data)),
        );
        return;
      case AdminStreamEvent.SessionEnded:
        this.dispatch(frame, (data) =>
          this.emitter.emit('sessionEnded', mapSessionState(data)),
        );
        return;
      default:
        return;
    }
  }
  private dispatch(frame: SseFrame, handle: (data: any) => void): void {
    let data: unknown;
    try {
      data = JSON.parse(frame.data);
    } catch {
      return;
    }
    handle(data);
  }
}
